import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  Switch,
  Icon,
  IconButton,
} from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../models/user-generated-contents/context';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { advocateSelectors } from '../../../models/advocate';

const DropdownMenuItemWithSwitch: React.FC<{
  icon: string;
  checked: boolean;
  onCheckedChange: () => void;
  actionName: string;
}> = ({ icon, checked, onCheckedChange, actionName }) => {
  const { t } = useTranslation();
  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        onCheckedChange();
      }}
      className="ugc_sidebar__settings_dropdown_item"
    >
      <div className="ugc_sidebar__settings_dropdown_item_name">
        <Icon>{icon}</Icon>
        <div>{actionName}</div>
      </div>
      <div className="ugc_sidebar__settings_dropdown_item_switch">
        <div aria-hidden className="text-sm">
          {t('common.off')}
        </div>
        <Switch checked={checked} />
        <div aria-hidden className="text-sm">
          {t('common.on')}
        </div>
      </div>
    </DropdownMenuItem>
  );
};

const SettingsDropdown = () => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateUserGeneratedContentFormState } =
    useUserGeneratedContent();

  const translationsEnabled = usePatronSelector(
    advocateSelectors.getCanTranslateSubmission
  );
  const commentingEnabled = usePatronSelector(
    advocateSelectors.getCanCommentSubmission
  );
  const sharingEnabled = usePatronSelector(
    advocateSelectors.getCanShareSubmission
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          iconName="settings"
          size="default"
          aria-label={t('common.settings')}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        className="ugc_sidebar__settings_dropdown"
      >
        {translationsEnabled && (
          <DropdownMenuItemWithSwitch
            icon="translate"
            checked={!!userGeneratedContent.is_translatable}
            onCheckedChange={() => {
              updateUserGeneratedContentFormState({
                is_translatable: !userGeneratedContent.is_translatable,
              });
            }}
            actionName={t('user_generated_content.translation')}
          />
        )}
        {commentingEnabled && (
          <DropdownMenuItemWithSwitch
            icon="mode_comment"
            checked={!!userGeneratedContent.is_commentable}
            onCheckedChange={() => {
              updateUserGeneratedContentFormState({
                is_commentable: !userGeneratedContent.is_commentable,
              });
            }}
            actionName={t('user_generated_content.commenting')}
          />
        )}
        {sharingEnabled && (
          <DropdownMenuItemWithSwitch
            icon="share"
            checked={!!userGeneratedContent.is_shareable}
            onCheckedChange={() => {
              updateUserGeneratedContentFormState({
                is_shareable: !userGeneratedContent.is_shareable,
              });
            }}
            actionName={t('user_generated_content.sharing')}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SettingsDropdown;
